// Profile tab: avatar, name/email/phone form fields.
'use client';

import { Controller, type UseFormReturn } from 'react-hook-form';
import type { UserProfile } from './profile-modal-helpers';
// BL-1170 — initials come from the shared IsomorphicService now; the local
// getInitials (which split the display string and mis-initialed particle
// surnames) was deleted.
import { staffInitials } from '@auctogon/core-admin/services/staff-names';
import type { StaffProfileFormValues } from '@auctogon/core-admin/schemas/staff-profile';
import { FIELD_LABEL_CLASS, FormField } from '@auctogon/ui/web';
import PhoneInput from '../../ui/PhoneInput';
import TextInput from '../../ui/TextInput';
import Avatar from '../../ui/Avatar';

/** Props for the profile editing tab. */
export interface ProfileTabContentProps {
  /** Read-only facts: the server-resolved display name and the email. */
  profile: UserProfile;
  /** The RHF form that owns the editable parts (BUG-2567). */
  form: UseFormReturn<StaffProfileFormValues>;
}

/**
 * Renders the profile editing form with avatar and input fields.
 *
 * BUG-211 PR-3: the "Your Roles" badge block was removed with the global
 * `staff_users.roles` column — a user's roles are now per-tenant and shown in
 * Settings → Users, not on the self-profile modal.
 * @param props - Current profile data and change handler
 */
export default function ProfileTabContent({ profile, form }: ProfileTabContentProps) {
  const { control, formState: { errors } } = form;
  return (
    <div className="space-y-6">
      {/* Avatar */}
      <div className="flex items-center space-x-4">
        {/*
          The `accent` tone is the BL-1170 token pair (bg-secondary /
          text-secondary-foreground) — BL-1701 moved the circle onto the shared
          Avatar so this modal stops owning its own 80px markup.
        */}
        <Avatar size="xl" tone="accent" initials={staffInitials(profile.name)} />
        <div>
          {/* The server-resolved display name — never blank (it falls back to
              the email address), so there is nothing to substitute here. */}
          <p className="text-body font-medium text-primary">{profile.name}</p>
          <p className="text-body text-gray-500">{profile.email}</p>
        </div>
      </div>

      {/*
        Name — TWO required fields, not one free-text box (BUG-1650 AC 3).
        `first_name` / `last_name` are the editable truth (BL-1170); the single
        "Full Name" box that used to be here submitted a composed string the
        route wrote to the column Better Auth owns, leaving both parts NULL — so
        a staff member who renamed themselves here became invisible to the Team
        page's Edit member modal and their notes were attributed to "Unknown".
      */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="First name" name="firstName" required error={errors.firstName?.message}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextInput
                id="firstName"
                data-testid="profile-first-name"
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
              />
            )}
          />
        </FormField>
        <FormField label="Last name" name="lastName" required error={errors.lastName?.message}>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextInput
                id="lastName"
                data-testid="profile-last-name"
                value={field.value}
                onChange={field.onChange}
                inputRef={field.ref}
              />
            )}
          />
        </FormField>
      </div>

      {/* Email (read-only) */}
      <div>
        <label htmlFor="profileEmail" className={FIELD_LABEL_CLASS}>
          Email address
        </label>
        <input
          id="profileEmail"
          type="email"
          value={profile.email}
          disabled
          className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-500 cursor-not-allowed"
        />
        <p className="mt-1 text-caption text-gray-500">Email cannot be changed</p>
      </div>

      {/* Phone */}
      <Controller
        name="phone"
        control={control}
        render={({ field }) => (
          <PhoneInput
            value={field.value ?? ''}
            onChange={field.onChange}
            inputRef={field.ref}
            label="Phone number"
            id="profilePhone"
            testId="profile-phone"
            error={errors.phone?.message}
          />
        )}
      />
    </div>
  );
}
